// How an on-sale title reads. Two small pieces, shared by the library's unlock
// line and the paywall plate so a sale looks the same wherever a reader meets
// it.
//
// SET LIKE A PRICE IN A PRINTED CATALOGUE, not like a promo badge. The house
// style is a book: Alegreya tabular numerals, hairline rules, let-spaced
// uppercase eyebrows. So the sale is a struck list price beside the new one and
// a small caps line above it — no pill, no gradient, no burst. The one
// concession to loudness is the ink: the eyebrow takes the app's status colour,
// which is also the only place on either surface that uses it.
//
// THE WORD CARRIES THE MEANING, NOT THE COLOUR. "On sale" is spelled out and the
// list price is struck through with a real `<s>`, so the offer survives
// grayscale, colour-blindness, and a screen reader — the colour is emphasis on
// top of a mark that already says it (dataviz: status colour never alone).

import { formatPrice, formatSaleEnds } from "@/lib/price";
import { cn } from "@bindery/ui/lib/utils";

/** The price side of `checkout.getProduct` — the only part these render. */
export interface SaleOffer {
  amountCents: number;
  listAmountCents: number;
  currency: string;
  onSale: boolean;
  saleEndsAt: number | null;
}

/**
 * The struck list price followed by what is actually charged. Renders a bare
 * price when nothing is on sale, so callers need no branch of their own.
 *
 * `emphasis` picks the weight for the surface: `inline` for a line of running
 * text (the library's unlock link), `plate` for the upsell's standalone figure.
 */
export function PriceAmount({
  offer,
  emphasis = "inline",
  className,
}: {
  offer: SaleOffer;
  emphasis?: "inline" | "plate";
  className?: string;
}) {
  const current = formatPrice(offer.amountCents, offer.currency);
  const list = formatPrice(offer.listAmountCents, offer.currency);
  const plate = emphasis === "plate";

  if (!offer.onSale) {
    return (
      <span
        className={cn(
          "font-serif lining-nums tabular-nums text-foreground",
          plate && "text-4xl",
          className,
        )}
      >
        {current}
      </span>
    );
  }

  return (
    <span className={cn("inline-flex items-baseline gap-2", className)}>
      {/* A real <s>, not a line-through class: the strike is what says "this
          is no longer the price", and it has to survive being read aloud. */}
      <s
        className={cn(
          "font-serif lining-nums tabular-nums text-muted-foreground/80 decoration-muted-foreground/60",
          plate && "text-2xl",
        )}
      >
        <span className="sr-only">Was </span>
        {list}
      </s>
      <span
        className={cn(
          "font-serif lining-nums tabular-nums text-foreground",
          plate && "text-4xl",
        )}
      >
        <span className="sr-only">now </span>
        {current}
      </span>
    </span>
  );
}

/**
 * The let-spaced "ON SALE" eyebrow, with a coarse countdown when the sale has an
 * end. Renders nothing when the offer isn't on sale, so it can sit
 * unconditionally in a layout.
 */
export function SaleMark({
  offer,
  now,
  className,
}: {
  offer: SaleOffer;
  now: number;
  className?: string;
}) {
  if (!offer.onSale) return null;
  const ends = formatSaleEnds(offer.saleEndsAt, now);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 font-serif text-[0.6875rem] tracking-[0.25em] text-destructive uppercase",
        className,
      )}
    >
      {/* A printer's fleuron rather than an icon — the surfaces around this are
          set in metal type, and a lucide glyph reads as UI chrome here. */}
      <span aria-hidden="true">§</span>
      On sale
      {ends !== null && (
        <span className="text-muted-foreground tracking-normal normal-case">
          {ends}
        </span>
      )}
    </span>
  );
}
